import React, { useState } from 'react';
import { Box, Typography, Stack, Divider } from '@mui/material';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { useAppContext } from '../../contexts/AppContextProvider';
import './style.css';
import { useUpdateAdminProfileMutation, useChangePasswordMutation } from '../../API/User';
import { failed, success } from '@components/Common/Toastify';
import TextInput from '@components/Common/TextInput';
import Button from '@components/Common/Button';
import { Users } from 'lucide-react';

const Profile = () => {
  const { value } = useAppContext();
  const user = value?.user;
  const [activeTab, setActiveTab] = useState<'profile' | 'password'>('profile');

  const updateProfileMutation = useUpdateAdminProfileMutation({
    onSuccess: () => {
      success('Profile updated successfully!');
    },
    onError: (error: any) => {
      failed(error?.response?.data?.message || 'Failed to update profile');
    },
  });

  const changePasswordMutation = useChangePasswordMutation({
    onSuccess: () => {
      success('Password changed successfully!');
      passwordFormik.resetForm();
    },
    onError: (error: any) => {
      failed(error?.response?.data?.message || 'Failed to change password');
    },
  });

  const profileFormik = useFormik({
    initialValues: {
      email: user?.email || '',
    },
    validationSchema: yup.object({
      email: yup.string().email('Invalid email address').required('Email is required'),
    }),
    enableReinitialize: true,
    onSubmit: (values) => {
      updateProfileMutation.mutate(values);
    },
  });

  const passwordFormik = useFormik({
    initialValues: {
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
    validationSchema: yup.object({
      oldPassword: yup.string().required('Current password is required'),
      newPassword: yup
        .string()
        .min(8, 'Password must be at least 8 characters')
        .required('New password is required'),
      confirmPassword: yup
        .string()
        .oneOf([yup.ref('newPassword')], 'Passwords must match')
        .required('Please confirm your password'),
    }),
    onSubmit: (values) => {
      changePasswordMutation.mutate({
        oldPassword: values.oldPassword,
        newPassword: values.newPassword,
        confirmPassword: values.confirmPassword,
      });
    },
  });

  return (
    <div className="main-dashboard-div">
      <div className="dashboard-wrapper">
        <div className="heading-dashboard">
          <div className="main-heading-div">
            <div className="flex items-center space-x-2">
              <Users className="h-6 w-6" />
              <h2>My Profile</h2>
            </div>
          </div>
        </div>

        <div className="profile-container">
          <Box sx={{ bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1, p: 4 }}>
            {/* Tabs */}
            <Stack direction="row" spacing={2} sx={{ mb: 4, borderBottom: 1, borderColor: 'divider' }}>
              <Button
                variant={activeTab === 'profile' ? 'primary' : 'secondary'}
                onClick={() => setActiveTab('profile')}
                className="tab-button"
              >
                Edit Profile
              </Button>
              <Button
                variant={activeTab === 'password' ? 'primary' : 'secondary'}
                onClick={() => setActiveTab('password')}
                className="tab-button"
              >
                Change Password
              </Button>
            </Stack>

            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <form onSubmit={profileFormik.handleSubmit}>
                <Stack spacing={3}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Update Email
                  </Typography>
                  
                  <TextInput
                    keyField="email"
                    label="Email Address"
                    type="email"
                    formik={profileFormik}
                    placeholder="Enter your email"
                    required
                  />

                  <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 3 }}>
                    <Button
                      variant="secondary"
                      onClick={() => profileFormik.resetForm()}
                      disabled={updateProfileMutation.isLoading}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={updateProfileMutation.isLoading}
                    >
                      {updateProfileMutation.isLoading ? 'Updating...' : 'Update Profile'}
                    </Button>
                  </Stack>
                </Stack>
              </form>
            )}

            {/* Password Tab */}
            {activeTab === 'password' && (
              <form onSubmit={passwordFormik.handleSubmit}>
                <Stack spacing={3}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Change Password
                  </Typography>
                  
                  <TextInput
                    keyField="oldPassword"
                    label="Current Password"
                    type="password"
                    formik={passwordFormik}
                    placeholder="Enter current password"
                    required
                  />

                  <TextInput
                    keyField="newPassword"
                    label="New Password"
                    type="password"
                    formik={passwordFormik}
                    placeholder="Enter new password"
                    required
                  />

                  <TextInput
                    keyField="confirmPassword"
                    label="Confirm New Password"
                    type="password"
                    formik={passwordFormik}
                    placeholder="Confirm new password"
                    required
                  />

                  <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 3 }}>
                    <Button
                      variant="secondary"
                      onClick={() => passwordFormik.resetForm()}
                      disabled={changePasswordMutation.isLoading}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={changePasswordMutation.isLoading}
                    >
                      {changePasswordMutation.isLoading ? 'Changing...' : 'Change Password'}
                    </Button>
                  </Stack>
                </Stack>
              </form>
            )}
          </Box>
        </div>
      </div>
    </div>
  );
};

export default Profile;

